












import { Component, Vue } from 'vue-property-decorator'
import { DoughnutChart } from 'vue-chart-3'
import {
    ActiveElement,
    ArcElement,
    Chart,
    ChartData,
    ChartEvent,
    ChartOptions,
    DoughnutController,
    LegendElement,
    LegendItem,
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { namespace } from 'vuex-class'
import { Pipeline } from '@/api/model/pipeline'
import { PipelinePhase } from '@/api/model/pipeline-phase'
import { ReferralStats } from '@/api/model/referral-stats'
import htmlLegend, { ChartOptionsWithHtmlLegend } from '@/helpers/charts/htmlLegend'

const referrals = namespace('referrals')
Chart.register(DoughnutController, ArcElement)

@Component({
    components: {
        DoughnutChart,
    },
})
export default class ChartReferralPhase extends Vue {
    @referrals.Getter readonly selectedPipeline!: Pipeline|null
    @referrals.Getter('selectablePipelines') readonly pipelines!: Pipeline[]
    @referrals.State readonly referralStats!: ReferralStats
    @referrals.State readonly phaseFilter!: PipelinePhase
    @referrals.Action applyPipelinePhaseFilter: ({ phase: PipelinePhase }) => Promise<void>

    get chartData(): ChartData {
        if (!this.chartValues) {
            return null
        }

        return {
            labels: this.chartLabels,
            datasets: [
                {
                    data: Object.values(this.chartValues),
                    backgroundColor: this.chartColors,
                    datalabels: {
                        color: 'rgba(255, 255, 255, .75)',
                    },
                },
            ],
        }
    }

    get chartOptions(): ChartOptionsWithHtmlLegend<'doughnut'> {
        return {
            responsive: true,
            maintainAspectRatio: true,
            aspectRatio: 1.5,
            animation: {
                easing: 'easeOutExpo',
            },
            plugins: {
                htmlLegend: {
                    containerID: 'legend-container',
                    activeIndex: Object.keys(this.chartValues).indexOf(this.phaseFilter?.id),
                    onClick: (item: LegendItem) => {
                        // @ts-ignore type defines datasetIndex but it is index at runtime
                        this.onClick(item.index)
                    },
                },
                legend: {
                    display: false,
                },
                tooltip: {
                    enabled: false,
                },
            },
            onClick: (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
                if (elements.length === 0) {
                    return
                }

                this.onClick(elements[0].index)
            },
        }
    }

    get chartPlugins(): object[] {
        return [
            ChartDataLabels,
            htmlLegend,
        ]
    }

    get chartValues(): {[key: string]: number} {
        if (!this.referralStats) {
            return {}
        }

        const chartValues = {}
        if (this.selectedPipeline) {
            this.selectedPipeline.activePhases.forEach(phase => {
                const entry = this.referralStats.countsPerPhase.find(v => v.phase === phase.id)
                chartValues[phase.id] = entry?.count ?? 0
            })
        }

        return chartValues
    }

    get chartLabels(): string[] {
        const chartLabels = []
        Object.keys(this.chartValues).forEach(id => {
            const phase = this.selectedPipeline.getPhase(id)
            chartLabels.push(`${(phase.name)} (${this.chartValues[id]})`)
        })

        return chartLabels
    }

    get chartColors(): string[] {
        return this.selectedPipeline.phases.map(phase => {
            return phase.color
        })
    }

    onClick(selectedIndex: number): void {
        const id = Object.keys(this.chartValues)[selectedIndex]

        if (id) {
            this.applyPipelinePhaseFilter({
                phase: this.selectedPipeline.getPhase(id),
            })
        }
    }
}
